import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import { useState, useEffect } from "react";
import { Project, ProjectDocument } from "types";
import ConfirmationModal from "@components/support/ConfirmationModal";

const getDocumentById = (
  project: Project,
  documentId: string,
): ProjectDocument | undefined => {
  for (const folder of project.folders) {
    const doc = folder.documents?.find((d) => d.id === documentId);
    if (doc) return doc;
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        const subDoc = sub.documents?.find((d) => d.id === documentId);
        if (subDoc) return subDoc;
      }
    }
  }
  return undefined;
};

const updateDocumentInProject = (
  project: Project,
  documentId: string,
  updater: (doc: ProjectDocument) => ProjectDocument,
): Project => {
  return {
    ...project,
    folders: project.folders.map((folder) => ({
      ...folder,
      documents: folder.documents?.map((d) =>
        d.id === documentId ? updater(d) : d,
      ),
      sub_folders: folder.sub_folders?.map((sub) => ({
        ...sub,
        documents: sub.documents?.map((d) =>
          d.id === documentId ? updater(d) : d,
        ),
      })),
    })),
  };
};

const removeDocumentFromProject = (
  project: Project,
  documentId: string,
): Project => {
  return {
    ...project,
    folders: project.folders.map((folder) => ({
      ...folder,
      documents: folder.documents?.filter((d) => d.id !== documentId),
      sub_folders: folder.sub_folders?.map((sub) => ({
        ...sub,
        documents: sub.documents?.filter((d) => d.id !== documentId),
      })),
    })),
  };
};

interface DocumentModalProps {
  onClose: () => void;
  document_id: string;
  activeProject: Project;
  setActiveProject?: (project: Project) => void;
  readOnly?: boolean;
}

export const DocumentModal = ({
  onClose,
  document_id,
  activeProject,
  setActiveProject,
  readOnly = false,
}: DocumentModalProps) => {
  const [document, setDocument] = useState<ProjectDocument | undefined>(
    getDocumentById(activeProject, document_id),
  );
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  useEffect(() => {
    const foundDoc = getDocumentById(activeProject, document_id);
    setDocument(foundDoc);
  }, [document_id, activeProject]);

  function handleToggleHidden() {
    if (!document || !setActiveProject) return;

    const updatedDoc = { ...document, hidden: !document.hidden };
    setDocument(updatedDoc);
    setActiveProject(
      updateDocumentInProject(activeProject, document.id, () => updatedDoc),
    );
  }

  function handleDelete() {
    if (!document || !setActiveProject) return;

    setActiveProject(removeDocumentFromProject(activeProject, document.id));
    onClose();
  }

  function handleOpenExternal() {
    if (document?.file?.url) {
      window.open(document.file.url, "_blank");
    }
  }

  if (!document) {
    return null;
  }

  return (
    <BaseModal
      width="max-w-800"
      title={document.name}
      startIcon={<Icon1 name="file-text" size={20} />}
      onClose={onClose}
    >
      {confirmationOpen && (
        <ConfirmationModal
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleDelete}
          title="Supprimer le document"
          message="Êtes-vous sûr de vouloir supprimer ce document ?"
          confirmText="CONFIRMER"
        />
      )}

      <div className="bg-white flex flex-col gap-4 p-4">
        {/* Document Display */}
        <div
          className="relative w-full rounded-xl overflow-hidden border"
          style={{ height: "500px" }}
        >
          <iframe
            src={document.file.url}
            title={document.name}
            className="w-full h-full"
            style={{ border: "none" }}
          />
          {document.hidden && (
            <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center pointer-events-none">
              <Icon1 name="invisible" size={32} color="white" />
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-row justify-center gap-2.5 items-center">
          <button
            onClick={handleOpenExternal}
            className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
            title="Ouvrir dans un nouvel onglet"
          >
            <Icon1 name="open" size={20} color="white" />
          </button>

          {!readOnly && (
            <button
              onClick={handleToggleHidden}
              className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
              title={document.hidden ? "Rendre visible" : "Masquer le document"}
            >
              <Icon1 name="invisible" size={24} color="white" />
            </button>
          )}

          {!readOnly && (
            <button
              onClick={() => setConfirmationOpen(true)}
              className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
              title="Supprimer le document"
            >
              <Icon1 name="delete" size={24} color="white" />
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <Button variant="small" label="Fermer" onClick={onClose} />
      </div>
    </BaseModal>
  );
};
